import type { PersistedState } from 'redux-persist';

interface RootState {
  reportQuery?: unknown;
  userQuery?: {
    cachedPages?: unknown;
    filters?: Array<unknown>;
    page?: number;
    pageSize?: number;
    sortField?: string;
    sortDir?: string;
  };
  userAccountQuery?: unknown;
  _persist?: {
    version: number;
    rehydrated: boolean;
  };
}

const DEFAULT_REPORT_SORT = {
  field: 'sightingDate',
  direction: 'desc',
} as const;

const DEFAULT_USER_ACCOUNT_QUERY = {
  filters: [],
  page: 0,
  pageSize: 10,
  sortField: 'createdAt',
  sortDir: 'desc',
} as const;

const USER_ACCOUNT_SORT_FIELD_ALIASES: Record<string, string> = {
  creationTime: 'createdAt',
  lastSignInTime: 'lastSignedInAt',
};

const USER_ACCOUNT_SORT_FIELDS = new Set(['email', 'displayName', 'createdAt', 'lastSignedInAt']);

const hasOwn = (state: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(state, key);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isReportSortDirection = (value: unknown): value is 'asc' | 'desc' =>
  value === 'asc' || value === 'desc';

const normalizeNonNegativeInteger = (value: unknown, fallback: number): number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : fallback;

const normalizePageSize = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
    return 10;
  }

  return Math.min(value, 100);
};

const normalizeUserAccountPageSize = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0 || value > 1000) {
    return DEFAULT_USER_ACCOUNT_QUERY.pageSize;
  }

  return value;
};

const isPersistedFilterValue = (value: unknown): boolean => {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null
  ) {
    return true;
  }

  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        typeof item === 'string' ||
        typeof item === 'number' ||
        typeof item === 'boolean' ||
        item === null
    )
  );
};

const normalizeFilterItems = (filters: unknown): Array<Record<string, unknown>> =>
  Array.isArray(filters)
    ? filters.filter(
        (filter): filter is Record<string, unknown> =>
          isRecord(filter) &&
          typeof filter.field === 'string' &&
          typeof filter.op === 'string' &&
          'value' in filter &&
          isPersistedFilterValue(filter.value)
      )
    : [];

const normalizeUserAccountSortField = (value: unknown): string | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  if (typeof value !== 'string') {
    return DEFAULT_USER_ACCOUNT_QUERY.sortField;
  }

  const normalized = USER_ACCOUNT_SORT_FIELD_ALIASES[value] ?? value;

  return USER_ACCOUNT_SORT_FIELDS.has(normalized)
    ? normalized
    : DEFAULT_USER_ACCOUNT_QUERY.sortField;
};

const normalizeUserAccountSortDir = (
  value: unknown,
  sortField: string | undefined
): 'asc' | 'desc' | undefined => {
  if (!sortField) {
    return undefined;
  }

  return isReportSortDirection(value) ? value : DEFAULT_USER_ACCOUNT_QUERY.sortDir;
};

const normalizeReportQueryState = (state: RootState): void => {
  if (!hasOwn(state, 'reportQuery')) {
    return;
  }

  const reportQuery = state.reportQuery;
  if (!isRecord(reportQuery)) {
    state.reportQuery = {
      filters: [],
      page: 0,
      pageSize: 10,
      sort: DEFAULT_REPORT_SORT,
    };
    return;
  }

  const reportSort = isRecord(reportQuery.sort) ? reportQuery.sort : undefined;
  const direction = isReportSortDirection(reportSort?.direction)
    ? reportSort.direction
    : isReportSortDirection(reportQuery.sortDir)
      ? reportQuery.sortDir
      : DEFAULT_REPORT_SORT.direction;

  state.reportQuery = {
    filters: normalizeFilterItems(reportQuery.filters),
    page: normalizeNonNegativeInteger(reportQuery.page, 0),
    pageSize: normalizePageSize(reportQuery.pageSize),
    sort: {
      field: DEFAULT_REPORT_SORT.field,
      direction,
    },
  };
};

const normalizeUserAccountQueryState = (state: RootState): void => {
  if (!hasOwn(state, 'userAccountQuery')) {
    return;
  }

  const userAccountQuery = state.userAccountQuery;
  if (!isRecord(userAccountQuery)) {
    state.userAccountQuery = { ...DEFAULT_USER_ACCOUNT_QUERY };
    return;
  }

  const sortField = normalizeUserAccountSortField(userAccountQuery.sortField);

  state.userAccountQuery = {
    filters: normalizeFilterItems(userAccountQuery.filters),
    page: normalizeNonNegativeInteger(userAccountQuery.page, DEFAULT_USER_ACCOUNT_QUERY.page),
    pageSize: normalizeUserAccountPageSize(userAccountQuery.pageSize),
    sortField,
    sortDir: normalizeUserAccountSortDir(userAccountQuery.sortDir, sortField),
  };
};

export const normalizePersistedRootState = (state: PersistedState): PersistedState => {
  if (!state) {
    return state;
  }

  normalizeReportQueryState(state as RootState);
  normalizeUserAccountQueryState(state as RootState);
  return state;
};

export const migrations = {
  0: (state: PersistedState): PersistedState => {
    if (!state) {
      return undefined;
    }
    const typedState = state as RootState;
    if (typedState.userQuery && !typedState.userAccountQuery) {
      typedState.userAccountQuery = {
        filters: typedState.userQuery.filters ?? [],
        page: typedState.userQuery.page ?? 0,
        pageSize: typedState.userQuery.pageSize ?? 10,
        sortField: typedState.userQuery.sortField,
        sortDir: typedState.userQuery.sortDir,
      };
    }
    if (typedState.userQuery?.cachedPages) {
      delete typedState.userQuery.cachedPages;
    }
    delete typedState.userQuery;
    return state;
  },
  2: (state: PersistedState): PersistedState => {
    return normalizePersistedRootState(state);
  },
};
